import { Edit } from '@mui/icons-material'
import { Masonry } from '@mui/lab'
import { Button, Grid } from '@mui/material'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { PersonDto } from 'dtos'
import { useRefresh } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import PersonHeroCard from '../PersonHeroCard'
import AddressesListCard from './AddressesListCard'
import DeclarantInformationCard from './DeclarantInformationCard'
import DriverInformationCard from './DriverInformationCard'
import HREmploymentInformationCard from './HREmploymentInformationCard'
import PersonAddEditDialog from './PersonAddEditDialog'
import VTS2AccessCard from './VTS2AccessCard'
import { GreenText } from 'components'
import DeclarationOfOwnershipSignerCard from './DeclarationOfOwnershipSignerCard'
import StatementOfConformitySignatureCard from './StatementOfConformitySignatureCard'

export default function PeopleId() {
  const { id } = useParams()
  const { permissions } = useAuth()

  const [isGettingPerson, setIsGettingPerson] = useState<boolean>(false)
  const [person, setPerson] = useState<PersonDto>(new PersonDto())

  const [refresh, handleRefresh] = useRefresh({
    root: 0,
    conformity: 0
  })

  useEffect(() => {
    if (id && id !== 'undefined' && id !== 'new') {
      getPerson()
    }
  }, [id])

  const getPerson = () => {
    setPerson(new PersonDto())

    if (id && id !== 'undefined' && id !== 'new') {
      setIsGettingPerson(true)
      controllers
        .getPersonById(id)
        .then(res => {
          console.log('Person: ', res.value)
          setPerson(res.value)
        })
        .finally(() => {
          setIsGettingPerson(false)
        })
    }
  }

  const [personAddEditDialogOpen, setPersonAddEditDialogOpen] = useState<boolean>(false)
  const [isUpdatingPerson, setIsUpdatingPerson] = useState<boolean>(false)

  return (
    <>
      <Fragment key={refresh.root}>
        <PersonAddEditDialog
          initialValues={person}
          loading={isUpdatingPerson}
          onClose={() => {
            setPersonAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsUpdatingPerson(true)
            controllers
              .updatePerson(values)
              .then(response => {
                setPerson(response.value)
                setPersonAddEditDialogOpen(false)
                enqueueSnackbar('Person Saved Successfully!', { variant: 'success' })
              })
              .finally(() => {
                setIsUpdatingPerson(false)
              })
          }}
          open={personAddEditDialogOpen}
        />

        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12}>
            <PersonHeroCard greenText={true} person={person}>
              {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    setPersonAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  EDIT
                </Button>
              )}
            </PersonHeroCard>
          </Grid>

          <Grid item xs={12}>
            <Masonry columns={3} sequential sx={{ margin: 0 /* this should be in the theme, otherwise it doesn't line up with hero card. */ }}>
              <AddressesListCard
                person={person}
                handleRefresh={() => {
                  handleRefresh('root')
                }}
              />

              {person.isDriver && <DriverInformationCard person={person} />}

              {person.isDeclarant && <DeclarantInformationCard person={person} />}

              {person.isDeclarationOfOwnershipSigner && <DeclarationOfOwnershipSignerCard person={person} />}

              {person.isEmployee && <HREmploymentInformationCard person={person} />}

              {permissions.USERS_ADD_EDIT && person.allowUserToSignIn && <VTS2AccessCard person={person} setPerson={setPerson} />}

              {person.isConformitySigner && <StatementOfConformitySignatureCard person={person} />}
            </Masonry>
          </Grid>
        </Grid>
      </Fragment>
      <GreenText variant='outlined'>
        {`**Person Name:**
        
Displays the person's name as entered in the Edit Person Dialog.

**Chips:**

Displays one or more chips indicating the person's access type(s). Possible chips 
include:

- Active
- Inactive
- Driver
- Declarant
- Employee
- Title Clerk
- Access to VTS2

**EDIT Button:**

Clicking the EDIT button opens the Edit Company Dialog.

**Company:**

Displays the company the person is associated with, as selected in the Edit Person Dialog.

**Title:**

Displays the person's title as entered in the Edit Person Dialog.

If no title has been entered, a “-” is displayed.

**Email Address:**

Displays the person's email address as entered in the Edit Person Dialog.

If no email address has been entered, a “-” is displayed.

**Mobile Phone:**

Displays the person's mobile phone number as entered in the Edit Person Dialog.

If no mobile phone number has been entered, a “-” is displayed.

**Office Phone:**

Displays the person's office phone number as entered in the Edit Person Dialog.

If no office phone number has been entered, a “-” is displayed.

**Notes:**

Displays any notes entered in the Edit Company Dialog.

If no notes has been entered, a “-” is displayed.

**Cards:**

Cards are displayed based on the features toggled on for the person.

Cards are arranged in alphabetical order, displayed from left to right. After three columns, the next row begins, following the same left-to-right arrangement.

        `}
      </GreenText>
    </>
  )
}
